import { Link } from 'react-router-dom';
import { 
  ChevronRight, Smartphone, Download, MapPin, Zap, CreditCard, 
  Bell, Shield, Star, Check, QrCode, Clock, Battery
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AppPage = () => {
  // Smart download link - detects OS and redirects
  const handleSmartDownload = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      window.location.href = 'https://apps.apple.com/hr/app/albmove/id6759310357';
      return;
    }
    
    // Android detection
    if (/android/i.test(userAgent)) {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.albmove.client&hl=sq';
      return;
    }
    
    // Default - show both options (desktop users)
    document.getElementById('download-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: MapPin,
      title: 'Gjeni Stacionet',
      description: 'Hartë interaktive me të gjitha stacionet e karikimit në Shqipëri. Shikoni disponueshmërinë në kohë reale.'
    },
    {
      icon: Zap,
      title: 'Nisni Karikimin',
      description: 'Filloni karikimin me një klikim. Skanoni QR kodin ose zgjidhni stacionin nga aplikacioni.'
    },
    {
      icon: CreditCard,
      title: 'Pagesë e Lehtë',
      description: 'Paguani automatikisht me kartë krediti, debit ose portofol dixhital. Pa para kesh.'
    },
    {
      icon: Bell,
      title: 'Njoftime',
      description: 'Merrni njoftime kur karikimi përfundon ose kur çmimi ndryshon.'
    },
    {
      icon: Shield,
      title: 'I Sigurt',
      description: 'Transaksione të sigurta dhe të dhëna të mbrojtura me enkriptim të avancuar.'
    },
    {
      icon: Clock,
      title: 'Historiku',
      description: 'Shikoni historikun e plotë të karikimeve dhe faturave tuaja.'
    }
  ];

  const steps = [
    { num: '1', title: 'Shkarkoni Aplikacionin', desc: 'Merreni falas nga App Store ose Google Play' },
    { num: '2', title: 'Krijoni Llogarinë', desc: 'Regjistrohuni me email ose numër telefoni' },
    { num: '3', title: 'Shtoni Metodën e Pagesës', desc: 'Lidhni kartën tuaj për pagesa automatike' },
    { num: '4', title: 'Filloni Karikimin', desc: 'Gjeni stacionin dhe nisni karikimin' }
  ];

  return (
    <div data-testid="app-page" className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#b0d33a] via-[#8fb82e] to-[#6a9a1f] text-white pt-32 pb-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-purple-200 text-sm mb-8">
            <Link to="/" className="hover:text-white transition-colors">Kryefaqja</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Aplikacioni AlbMove</span>
          </nav>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm font-medium">Aplikacioni Zyrtar</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold font-['Inter_Tight'] mb-6">
                Karikimi në<br />
                <span className="text-slate-900">Xhepin Tuaj</span>
              </h1>
              
              <p className="text-lg text-slate-800 mb-8 max-w-lg">
                Me aplikacionin AlbMove, menaxhoni karikimin e automjetit tuaj elektrik nga çdo vend. 
                Gjeni stacionet, nisni karikimin dhe paguani me lehtësi.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleSmartDownload}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 font-semibold text-lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Shkarko Tani
                </Button>
                
                <div className="flex items-center gap-2 text-slate-800">
                  <div className="flex -space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm">4.8/5 vlerësim</span>
                </div>
              </div>
            </div>
            
            <div className="relative hidden lg:flex justify-center items-center min-h-[500px]">
              {/* Samsung mockup - mbrapa */}
              <img 
                src="/images/mockups/samsung-mockup.png"
                alt="AlbMove App - Android"
                className="w-72 h-auto drop-shadow-2xl absolute -left-4 top-12 -rotate-6 opacity-90 hover:opacity-100 transition-opacity"
              />
              {/* iPhone mockup - para */}
              <img 
                src="/images/mockups/iphone-mockup.png"
                alt="AlbMove App - iPhone"
                className="w-80 h-auto drop-shadow-2xl relative z-10 rotate-3 hover:rotate-0 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#b0d33a] mb-4">
              Funksionalitetet
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Gjithçka në Një Aplikacion
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Aplikacioni AlbMove ju ofron të gjitha mjetet që ju duhen për të menaxhuar karikimin e automjetit tuaj elektrik.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200/50 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#b0d33a]/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#7a9c1f]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#b0d33a] mb-4">
              Si Funksionon
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Filloni në 4 Hapa
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center relative">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-[#b0d33a]/30" />
                )}
                <div className="w-16 h-16 rounded-full bg-[#b0d33a] text-slate-900 flex items-center justify-center text-2xl font-bold mx-auto mb-6 relative z-10">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Download Section */}
      <section id="download-section" className="py-20 bg-gradient-to-br from-[#b0d33a] via-[#8fb82e] to-[#6a9a1f]">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Shkarkoni Aplikacionin Tani
          </h2>
          <p className="text-lg text-slate-800 mb-10 max-w-xl mx-auto">
            Disponueshëm falas për iOS dhe Android. Filloni karikimin e automjetit tuaj më lehtë se kurrë.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="https://apps.apple.com/hr/app/albmove/id6759310357"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-8 py-4 font-medium hover:bg-gray-900 transition-all hover:scale-105"
            >
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs opacity-70">Shkarko në</p>
                <p className="text-xl font-semibold">App Store</p>
              </div>
            </a>
            
            <a 
              href="https://play.google.com/store/apps/details?id=com.albmove.client&hl=sq"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-8 py-4 font-medium hover:bg-gray-900 transition-all hover:scale-105"
            >
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs opacity-70">Merreni në</p>
                <p className="text-xl font-semibold">Google Play</p>
              </div>
            </a>
          </div>
          
          {/* QR Code */}
          <div className="mt-12">
            <p className="text-slate-800 mb-4">Ose skanoni QR kodin:</p>
            <div className="inline-flex items-center justify-center bg-white p-4 rounded-2xl">
              <QrCode className="w-32 h-32 text-slate-900" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AppPage;
